import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const PermissionToggle = ({ label, checked, onChange }) => (
  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 13, cursor: 'pointer' }}>
    {label}
    <input type="checkbox" checked={checked} onChange={onChange} style={{ accentColor: 'var(--cr-primary)', width: 18, height: 18 }} />
  </label>
);

const ApproveModal = ({ membership, onClose, onSave }) => {
  const [role, setRole] = useState(membership.role || 'member');
  const [perms, setPerms] = useState({
    can_schedule: membership.can_schedule, can_view_horses: membership.can_view_horses,
    can_add_notes: membership.can_add_notes, can_message: membership.can_message !== false,
    can_send_documents: membership.can_send_documents, can_send_invites: membership.can_send_invites,
    can_view_horse_checks: !!membership.can_view_horse_checks, can_record_horse_checks: !!membership.can_record_horse_checks, horse_checks_all_horses: !!membership.horse_checks_all_horses,
  });
  const [saving, setSaving] = useState(false);
  const tog = (k) => () => setPerms(p => ({ ...p, [k]: !p[k] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/organizations/members/${membership.id}/approve`, { role, ...perms });
      toast.success('Member approved');
      onSave(data);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Approve {membership.account?.first_name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <div className="chip-group">
            <div className={`chip ${role === 'member' ? 'selected' : ''}`} onClick={() => setRole('member')}>Member</div>
            <div className={`chip ${role === 'trainer' ? 'selected' : ''}`} onClick={() => setRole('trainer')}>Trainer</div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
          <div className="form-label" style={{ marginBottom: 4 }}>Permissions in this organization</div>
          <PermissionToggle label="Schedule lessons & events" checked={perms.can_schedule} onChange={tog('can_schedule')} />
          <PermissionToggle label="View horses (if owner shares)" checked={perms.can_view_horses} onChange={tog('can_view_horses')} />
          <PermissionToggle label="View daily horse checks" checked={perms.can_view_horse_checks} onChange={tog('can_view_horse_checks')} />
          <PermissionToggle label="Record daily horse checks" checked={perms.can_record_horse_checks} onChange={tog('can_record_horse_checks')} />
          <PermissionToggle label="Daily checks for all barn horses (not just assigned)" checked={perms.horse_checks_all_horses} onChange={tog('horse_checks_all_horses')} />
          <PermissionToggle label="Add training notes" checked={perms.can_add_notes} onChange={tog('can_add_notes')} />
          <PermissionToggle label="Message members" checked={perms.can_message} onChange={tog('can_message')} />
          <PermissionToggle label="Send documents for signature" checked={perms.can_send_documents} onChange={tog('can_send_documents')} />
          <PermissionToggle label="Send invites to the barn" checked={perms.can_send_invites} onChange={tog('can_send_invites')} />
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 12px' }}>
          Viewing a horse's records also requires the horse owner to share that horse with this organization.
        </p>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Approve member'}</button>
      </div>
    </div>
  );
};


const ROLE_LABELS = { manager: 'Barn Owner', trainer: 'Trainer', staff: 'Staff', boarder: 'Boarder', member: 'Member' };
const ROLE_CHIPS = [
  ['manager', 'Barn Owner'],
  ['trainer', 'Trainer'],
  ['staff', 'Staff'],
  ['boarder', 'Boarder'],
  ['member', 'Member'],
];

const ROLE_HINTS = {
  manager: 'Owns or runs the barn. Full access, can assign work.',
  trainer: 'Teaches and schedules. Sees feed plans.',
  staff: 'Grooms, stable hands, working students. Completes tasks.',
  boarder: 'Keeps a horse here. Messaging only by default.',
  member: 'Basic access. Messaging only by default.',
};

const EditMemberModal = ({ membership, onClose, onSave }) => {
  const [role, setRole] = useState(membership.role || 'member');
  const [jobTitle, setJobTitle] = useState(membership.job_title || '');
  const [perms, setPerms] = useState({
    can_schedule: !!membership.can_schedule,
    can_view_horses: !!membership.can_view_horses,
    can_add_notes: !!membership.can_add_notes,
    can_message: membership.can_message !== false,
    can_send_documents: !!membership.can_send_documents,
    can_send_invites: !!membership.can_send_invites,
    can_complete_tasks: !!membership.can_complete_tasks,
    can_assign_tasks: !!membership.can_assign_tasks,
    can_view_feed_plans: !!membership.can_view_feed_plans,
    can_view_horse_checks: !!membership.can_view_horse_checks,
    can_record_horse_checks: !!membership.can_record_horse_checks,
    horse_checks_all_horses: !!membership.horse_checks_all_horses,
  });
  const [usePreset, setUsePreset] = useState(false);
  const [offersLessons, setOffersLessons] = useState(!!membership.offers_lessons);
  const [saving, setSaving] = useState(false);
  const tog = (k) => () => { setUsePreset(false); setPerms(p => ({ ...p, [k]: !p[k] })); };

  const pickRole = (r) => { setRole(r); setUsePreset(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { role, job_title: jobTitle, apply_preset: usePreset };
      if (!usePreset) Object.assign(body, perms);
      const { data } = await api.put(`/organizations/members/${membership.id}`, body);
      if (offersLessons !== !!membership.offers_lessons) {
        await api.put(`/booking/memberships/${membership.id}/offers-lessons`, { offers_lessons: offersLessons });
      }
      toast.success('Member updated');
      onSave(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 17 }}>{membership.account?.first_name} {membership.account?.last_name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>&times;</button>
        </div>

        <div className="form-group">
          <label className="form-label">Role</label>
          <div className="chip-group" style={{ flexWrap: 'wrap' }}>
            {ROLE_CHIPS.map(([v, l]) => (
              <div key={v} className={`chip ${role === v ? 'selected' : ''}`} onClick={() => pickRole(v)}>{l}</div>
            ))}
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{ROLE_HINTS[role]}</p>
          {usePreset && (
            <p style={{ fontSize: 11, color: 'var(--cr-primary)', marginTop: 4 }}>
              Saving will apply the standard permissions for this role.
            </p>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Job title (optional)</label>
          <input className="form-input" placeholder="e.g. Head Groom, Working Student"
            value={jobTitle} onChange={e => setJobTitle(e.target.value)} maxLength={120} />
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
          <div className="form-label" style={{ marginBottom: 4 }}>Permissions in this organization</div>
          <PermissionToggle label="Offers lessons (bookable by members)" checked={offersLessons} onChange={() => setOffersLessons(v => !v)} />
          <PermissionToggle label="Schedule lessons & events" checked={perms.can_schedule} onChange={tog('can_schedule')} />
          <PermissionToggle label="View horses (if owner shares)" checked={perms.can_view_horses} onChange={tog('can_view_horses')} />
          <PermissionToggle label="View daily horse checks" checked={perms.can_view_horse_checks} onChange={tog('can_view_horse_checks')} />
          <PermissionToggle label="Record daily horse checks" checked={perms.can_record_horse_checks} onChange={tog('can_record_horse_checks')} />
          <PermissionToggle label="Daily checks for all barn horses (not just assigned)" checked={perms.horse_checks_all_horses} onChange={tog('horse_checks_all_horses')} />
          <PermissionToggle label="View feed & supplement plans" checked={perms.can_view_feed_plans} onChange={tog('can_view_feed_plans')} />
          <PermissionToggle label="Complete daily tasks" checked={perms.can_complete_tasks} onChange={tog('can_complete_tasks')} />
          <PermissionToggle label="Assign tasks to others" checked={perms.can_assign_tasks} onChange={tog('can_assign_tasks')} />
          <PermissionToggle label="Add training notes" checked={perms.can_add_notes} onChange={tog('can_add_notes')} />
          <PermissionToggle label="Message members" checked={perms.can_message} onChange={tog('can_message')} />
          <PermissionToggle label="Send documents for signature" checked={perms.can_send_documents} onChange={tog('can_send_documents')} />
          <PermissionToggle label="Send invites to the barn" checked={perms.can_send_invites} onChange={tog('can_send_invites')} />
        </div>

        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 12px' }}>
          Viewing a horse's records also requires the horse owner to share that horse with this organization.
        </p>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

const InviteEmailModal = ({ org, onClose }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) return toast.error('Enter an email address');
    setSaving(true);
    try {
      await api.post(`/organizations/${org.id}/invite-email`, { email, message });
      toast.success('Invite email sent');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to send invite');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Invite member</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ background: 'var(--cr-primary-light)', borderRadius: 12, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Invite to</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--cr-primary)' }}>{org.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Sent as {org.name} via Connected Rider. Replies go to the barn manager email.</div>
        </div>

        <div className="form-group">
          <label className="form-label">Email address</label>
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="person@email.com" autoComplete="email" autoFocus />
        </div>

        <div className="form-group">
          <label className="form-label">Optional message</label>
          <textarea className="form-input" value={message} onChange={e => setMessage(e.target.value)} rows={3} placeholder={`Hi, you've been invited to join ${org.name} on Connected Rider.`} />
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Invite code included in email</div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 3, color: 'var(--cr-primary)' }}>{org.invite_code}</div>
        </div>

        <button className="btn btn-primary btn-full" onClick={handleSend} disabled={saving}>{saving ? 'Sending...' : 'Send invite email'}</button>
      </div>
    </div>
  );
};

const OrgDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [isController, setIsController] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [showInviteEmail, setShowInviteEmail] = useState(false);
  const [tab, setTab] = useState('members');

  const load = async () => {
    try {
      const orgRes = await api.get(`/organizations/${id}`);
      setOrg(orgRes.data);
      const myOrgs = await api.get('/organizations/mine');
      const mine = myOrgs.data.find(o => o.id === id);
      const controller = mine && mine.my_role === 'manager';
      setIsController(controller);
      if (controller) {
        const memRes = await api.get(`/organizations/${id}/members`);
        setMembers(memRes.data);
      }
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const handleRemove = async (membershipId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await api.delete(`/organizations/members/${membershipId}`);
      toast.success('Member removed');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this organization? Your horses and data leave with you.')) return;
    try {
      await api.post(`/organizations/${id}/leave`);
      toast.success('You have left the organization');
      navigate('/organizations');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!org) return <div className="page-content"><p>Organization not found.</p></div>;

  const pending = members.filter(m => m.status === 'pending');
  const active = members.filter(m => m.status === 'active');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/organizations')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: 'white', fontSize: 17 }}>{org.my_role === 'manager' ? 'Manage barn' : org.name}</h1>
            {org.my_role === 'manager' && <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0 }}>{org.name}</p>}
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{org.org_type === 'barn' ? 'Barn' : 'Training business'}{org.location ? ` · ${org.location}` : ''}</p>
          </div>
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {isController && (
          <div className="card" style={{ background: 'var(--cr-primary-light)', border: '1px solid var(--cr-primary)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Invite code — share to let people request to join</div>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: 3, color: 'var(--cr-primary)', marginBottom: 10 }}>{org.invite_code}</div>
            <button className="btn btn-primary btn-full" onClick={() => setShowInviteEmail(true)} style={{ marginBottom: 8 }}>Send email invite</button>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                navigator.clipboard.writeText(org.invite_code).then(() => toast.success('Invite code copied!')).catch(() => toast.error('Could not copy'));
              }}>Copy code</button>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const link = `${window.location.origin}/organizations?join=${org.invite_code}`;
                navigator.clipboard.writeText(link).then(() => toast.success('Invite link copied!')).catch(() => toast.error('Could not copy'));
              }}>Copy link</button>
            </div>
          </div>
        )}

        {isController && (
          <div className="tab-bar" style={{ marginBottom: 12, borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            <div className={`tab-item ${tab === 'members' ? 'active' : ''}`} onClick={() => setTab('members')}>Members</div>
            <div className={`tab-item ${tab === 'documents' ? 'active' : ''}`} onClick={() => navigate(`/organizations/${id}/documents`)}>Documents</div>
            {org.my_role === 'manager' && <div className="tab-item" onClick={() => navigate('/staff')}>Staff</div>}
          </div>
        )}

        {isController && pending.length > 0 && (
          <>
            <div className="section-label">Pending requests ({pending.length})</div>
            {pending.map(m => (
              <div key={m.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="avatar avatar-md">{m.account?.first_name?.[0]}{m.account?.last_name?.[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.account?.first_name} {m.account?.last_name?.[0]}.</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Wants to join</div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setApproving(m)}>Review</button>
              </div>
            ))}
          </>
        )}

        {isController && (
          <>
            <div className="section-label">Members ({active.length})</div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 10px' }}>Tap Edit to manage roles, permissions, and lessons.</p>
            {active.map(m => (
              <div key={m.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="avatar avatar-md">{m.account?.first_name?.[0]}{m.account?.last_name?.[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.account?.first_name} {m.account?.last_name?.[0]}.</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {ROLE_LABELS[m.role] || m.role}{m.job_title ? ' \u00b7 ' + m.job_title : ''}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.account?.email}</div>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setEditingMember(m)}>Edit</button>
                {m.role !== 'manager' && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleRemove(m.id)}>Remove</button>
                )}
              </div>
            ))}
          </>
        )}

        {!isController && (
          <>
            <div className="card">
              <h3 style={{ marginBottom: 8 }}>About</h3>
              {org.description && <p style={{ fontSize: 13, marginBottom: 8 }}>{org.description}</p>}
              {org.phone && <div style={{ fontSize: 13, marginBottom: 4 }}>📞 {org.phone}</div>}
              {org.email && <div style={{ fontSize: 13, marginBottom: 4 }}>✉️ {org.email}</div>}
              {org.disciplines && <div style={{ fontSize: 13 }}>🏇 {org.disciplines}</div>}
            </div>
            <button className="btn btn-secondary btn-full" onClick={() => navigate(`/organizations/${id}/documents`)} style={{ marginBottom: 8 }}>View documents</button>
            <button className="btn btn-danger btn-full" onClick={handleLeave}>Leave organization</button>
          </>
        )}
      </div>

      {approving && <ApproveModal membership={approving} onClose={() => setApproving(null)} onSave={() => { setApproving(null); load(); }} />}
      {editingMember && <EditMemberModal membership={editingMember} onClose={() => setEditingMember(null)} onSave={() => { setEditingMember(null); load(); }} />}
      {showInviteEmail && <InviteEmailModal org={org} onClose={() => setShowInviteEmail(false)} />}
    </div>
  );
};

export default OrgDetailPage;
